import type { AiPrompt } from "@/types/ai-prompt";
import type { PayloadToken } from "@/types/ai-prompt";
import type { DiffEntry } from "./types";

export function getActiveVersionText(prompt: AiPrompt): string {
  const activeVersion = prompt.versions.find((v) => v.is_active);
  return activeVersion ? activeVersion.prompt_text : prompt.default_prompt;
}

export function formatVersionDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) {
    return `Today ${date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
  }
  if (diffDays === 1) return "Yesterday";
  return date.toLocaleDateString([], { month: "short", day: "numeric" });
}

export function parseTokensFromText(text: string): string[] {
  const matches = text.match(/\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g);
  if (!matches) return [];
  return [...new Set(matches.map((m) => m.slice(1, -1)))];
}

export function statusBadgeClass(status: PayloadToken["status"]): string {
  if (status === "available") return "bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20";
  if (status === "needs_work") return "bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/20";
  return "bg-muted text-muted-foreground border-border";
}

export function statusLabel(status: PayloadToken["status"]): string {
  if (status === "available") return "Available";
  if (status === "needs_work") return "Needs Work";
  return "Planned";
}

export function tokenBadgeClass(status: PayloadToken["status"] | undefined): string {
  if (status === "available") return "bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20";
  if (status === "needs_work") return "bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/20";
  return "bg-muted/60 text-muted-foreground border-border";
}

export function isValidConfigNumber(value: string): boolean {
  if (value === "") return false;
  const n = Number(value);
  return Number.isFinite(n) && Number.isInteger(n) && n >= 0;
}

export function computeLineDiff(left: string, right: string): DiffEntry[] {
  const a = left.split("\n");
  const b = right.split("\n");
  const out: DiffEntry[] = [];

  const n = a.length;
  const m = b.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push({ side: "left", type: "context", text: a[i] });
      out.push({ side: "right", type: "context", text: b[j] });
      i++; j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      out.push({ side: "left", type: "removed", text: a[i] });
      out.push({ side: "right", type: "context", text: "" });
      i++;
    } else {
      out.push({ side: "left", type: "context", text: "" });
      out.push({ side: "right", type: "added", text: b[j] });
      j++;
    }
  }
  while (i < n) {
    out.push({ side: "left", type: "removed", text: a[i] });
    out.push({ side: "right", type: "context", text: "" });
    i++;
  }
  while (j < m) {
    out.push({ side: "left", type: "context", text: "" });
    out.push({ side: "right", type: "added", text: b[j] });
    j++;
  }
  return out;
}
